/**
 * Recognize a chat share link pasted on its own.
 *
 * Shared by the editor (a pasted link triggers an import instead of landing
 * as text), the import API (which only ever fetches a URL it rebuilt from the
 * validated id — never the string the user sent), and the landing page's
 * link box. Keep it dependency-free: it runs in the browser and on the server.
 */
export type ShareSite = "chatgpt" | "claude" | "gemini";

export interface ShareLink {
  site: ShareSite;
  id: string;
  /** Canonical URL rebuilt from the id — the only URL the server fetches. */
  url: string;
}

export const SHARE_SITE_LABEL: Record<ShareSite, string> = {
  chatgpt: "ChatGPT",
  claude: "Claude",
  gemini: "Gemini",
};

const UUID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

const PATTERNS: { site: ShareSite; re: RegExp; url: (id: string) => string; keepCase?: boolean }[] = [
  {
    site: "chatgpt",
    re: new RegExp(`^https?://(?:chatgpt\\.com|chat\\.openai\\.com)/share/(${UUID})(?:[/?#].*)?$`, "i"),
    url: (id) => `https://chatgpt.com/share/${id}`,
  },
  {
    site: "claude",
    re: new RegExp(`^https?://claude\\.ai/share/(${UUID})(?:[/?#].*)?$`, "i"),
    url: (id) => `https://claude.ai/share/${id}`,
  },
  {
    site: "gemini",
    re: /^https?:\/\/(?:gemini\.google\.com\/share|g\.co\/gemini\/share)\/([0-9a-f]{6,32})(?:[/?#].*)?$/i,
    url: (id) => `https://gemini.google.com/share/${id}`,
  },
  {
    // Short form shown on the share page itself; the server follows its
    // redirect to learn the long id. The code is case-sensitive.
    site: "gemini",
    re: /^https?:\/\/share\.gemini\.google\/([A-Za-z0-9_-]{6,64})(?:[/?#].*)?$/,
    url: (id) => `https://share.gemini.google/${id}`,
    keepCase: true,
  },
];

/**
 * The whole (trimmed) text must be one share URL — a link inside a paragraph
 * is content, not an import request.
 */
export function parseShareLink(text: string): ShareLink | null {
  const t = text.trim();
  if (!t || t.length > 300 || /\s/.test(t)) return null;
  for (const p of PATTERNS) {
    const m = p.re.exec(t);
    if (m) {
      const id = p.keepCase ? m[1] : m[1].toLowerCase();
      return { site: p.site, id, url: p.url(id) };
    }
  }
  return null;
}

/**
 * The whole (trimmed) text is one http(s) URL — to any site. Used to tell a
 * visitor who pasted a link we cannot import (an abacus.ai chat, a Claude
 * page, an article) what to do instead of converting a bare URL.
 */
export function parseLoneUrl(text: string): URL | null {
  const t = text.trim();
  if (!t || t.length > 2000 || /\s/.test(t) || !/^https?:\/\//i.test(t)) return null;
  try {
    return new URL(t);
  } catch {
    return null;
  }
}

